import React, { useCallback, useEffect, useRef, useState } from 'react'
import './style.css'
import dayjs, { Dayjs } from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { Alert, Button, Calendar, Row, Col, Select } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../../../hooks/useAppDispatch/useAppDispatch'
import { getTasksListToCalendar } from '../../../../../store/calendar/action-creators'
import {
  createSession,
  getTaskCalendarData,
  getTaskCalendarDataLoading,
  getTaskCalendarError,
  IDataByDayItem,
} from '../../model/selectors'
import { getFilterDateRange } from '../../model/lib/getFilterDateRange'
import { getPreviewTaskItemData } from '../../model/services/getPreviewTaskItemData'
import { getDetailTaskItemData } from '../../model/services/getDetailTaskItemData'
import { TaskDetailModal } from '../task-detail-modal/task-detail-modal'
import { DefaultCellTemplate } from '../cell-templates/default/default'
import { HoursCellTemplate } from '../cell-templates/hours/hours'
import { getPreviewTaskItemDataByTime } from '../../model/services/getPreviewTaskItemDataByTime'
import { getDetailTimeSlotData } from '../../model/services/getDetailTimeSlotData'
import { WeekView } from '../week-view/week-view'
import { DayView } from '../day-view/day-view'
import { ModeSelector } from '../mode-selector'
import styled from 'styled-components'
import { useLocation } from 'react-router-dom'

dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)

interface TaskCalendarProps {
  isMobile?: boolean
}

const CalendarContainer = styled.div`
  .ant-picker-calendar {
    background: #fff;
  }

  .ant-picker-calendar .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-calendar-date {
    background: #e6f7ff;
    border: 2px solid #1890ff;
  }

  .ant-picker-calendar .ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-calendar-date {
    border-color: #1890ff;
  }

  .ant-picker-calendar
    .ant-picker-cell-in-view.ant-picker-cell-today
    .ant-picker-calendar-date-value {
    color: #1890ff;
    font-weight: 600;
  }

  .ant-picker-calendar-date:hover {
    background: #fafafa;
    cursor: pointer;
  }

  .year-month-cell {
    position: relative;
    padding: 8px;
    min-height: 80px;
    cursor: pointer;
    transition: all 0.3s;
  }

  .year-month-cell-stats {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 11px;
  }

  .year-month-cell-stat {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .year-month-cell-stat-badge {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;

    &.expired {
      background-color: #ff4d4f;
    }

    &.new {
      background-color: #52c41a;
    }
  }

  .ant-picker-calendar-date-content {
    overflow: hidden;
  }

  .calendar-date-cell {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 4px;

    .year-month-cell-stat {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 8px;
      border-radius: 4px;
      background: #fafafa;
      font-size: 12px;
      transition: all 0.2s;

      &:hover {
        background: #f0f0f0;
        transform: translateX(2px);
      }

      &.expired {
        background: #fff1f0;
        border-left: 3px solid #ff4d4f;
      }

      &.new {
        background: #f6ffed;
        border-left: 3px solid #52c41a;
      }
    }
  }

  @media (max-width: 768px) {
    .ant-picker-calendar {
      font-size: 12px;
    }

    .ant-picker-calendar-header {
      padding: 8px !important;
    }

    .ant-picker-calendar-header .ant-select {
      font-size: 12px !important;
    }

    .ant-picker-calendar-header .ant-radio-group {
      font-size: 11px !important;
    }

    .ant-picker-calendar-header .ant-radio-button-wrapper {
      padding: 2px 8px !important;
      font-size: 11px !important;
    }

    .ant-picker-cell {
      padding: 2px !important;
    }

    .ant-picker-calendar-date {
      padding: 2px !important;
      margin: 0 !important;
      font-size: 11px;
    }

    .ant-picker-calendar-date-value {
      font-size: 14px !important;
      line-height: 20px !important;
    }

    .ant-picker-calendar-date-content {
      height: auto !important;
      min-height: 40px;
      font-size: 10px;
      overflow: hidden;
    }

    .ant-picker-calendar-month-panel,
    .ant-picker-calendar-year-panel {
      padding: 4px !important;
    }

    .ant-picker-cell-inner {
      padding: 4px !important;
      font-size: 12px !important;
    }

    .year-month-cell {
      padding: 4px;
      min-height: 60px;
    }

    .year-month-cell-stats {
      font-size: 10px;
      gap: 2px;
    }

    .year-month-cell-stat-badge {
      width: 6px;
      height: 6px;
    }

    .ant-picker-calendar-date-content {
      .ant-badge {
        .ant-badge-status-text {
          font-size: 9px;
          font-weight: 600;
        }

        .ant-badge-status-text[data-mobile='true'] {
          font-size: 0;

          &::before {
            content: attr(data-count);
            font-size: 9px;
            font-weight: 600;
            display: inline-block;
          }
        }
      }
    }
  }

  @media (max-width: 576px) {
    .ant-picker-calendar {
      font-size: 11px;
    }

    .ant-picker-calendar-header {
      padding: 4px !important;
      flex-direction: column;
      gap: 8px;
    }

    .ant-picker-calendar-header .ant-radio-button-wrapper {
      padding: 2px 6px !important;
      font-size: 10px !important;
    }

    .ant-picker-calendar-date-content {
      min-height: 30px;
      font-size: 9px;
    }

    .ant-picker-calendar-date-value {
      font-size: 12px !important;
      line-height: 18px !important;
    }
  }
`

export const TasksCalendarComponent = ({ isMobile = false }: TaskCalendarProps) => {
  const { sessionData } = useSelector(createSession)
  const location = useLocation()
  const dispatch = useAppDispatch()

  const dataByDay = useSelector(getTaskCalendarData)
  const isLoading = useSelector(getTaskCalendarDataLoading)
  const error = useSelector(getTaskCalendarError)

  const isLeader = sessionData?.roles?.includes(19) ?? false

  const [isOpenModal, setIsOpenModal] = useState<boolean>(false)

  const [value, setValue] = useState<Dayjs>(() => dayjs())
  const [localMode, setLocalMode] = useState<'year' | 'month' | 'week' | 'day'>('month')
  const [isTimeTableViewMode, setIsTimeTableViewMode] = useState<boolean>(true)

  const [currentDetailData, setCurrentDetailData] = useState<IDataByDayItem[]>([])
  const targetDate = useRef(dayjs())

  const getFilterFromUrl = useCallback(() => {
    const params = new URLSearchParams(location.search)
    const filter: any = {
      dates: getFilterDateRange(targetDate.current, localMode),
    }

    const id = params.get('id')
    if (id) filter.id = id

    const stage = params.get('stage')
    if (stage) filter.stage = stage

    const taskType = params.get('task_type')
    if (taskType) filter.task_type = taskType

    const responsible = params.get('responsible')
    if (responsible && !isNaN(+responsible)) filter.responsible = +responsible

    const dates = params.get('dates')
    if (dates) filter.dates = dates

    if (!filter.stage) {
      filter.stage = 'notDone'
    }

    return filter
  }, [location.search, localMode])

  useEffect(() => {
    if (!sessionData?.user?.id) {
      return
    }
    const filter = getFilterFromUrl()
    dispatch(getTasksListToCalendar(filter))
  }, [location.search, localMode, sessionData?.user?.id, dispatch, getFilterFromUrl])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768 && isTimeTableViewMode && localMode === 'month') {
        setIsTimeTableViewMode(false)
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isTimeTableViewMode, localMode])

  const onSelect = useCallback((newValue: Dayjs) => {
    setValue(newValue)
  }, [])

  const handleValueChange = useCallback(
    (newValue: Dayjs) => {
      setValue(newValue)
      targetDate.current = newValue

      if (!isLeader && !sessionData?.user?.id) {
        return
      }

      const params = new URLSearchParams(location.search)
      const filter: any = {
        dates: getFilterDateRange(newValue, localMode),
      }

      const id = params.get('id')
      if (id) filter.id = id

      const stage = params.get('stage')
      if (stage) {
        filter.stage = stage
      } else {
        filter.stage = 'notDone'
      }

      const taskType = params.get('task_type')
      if (taskType) filter.task_type = taskType

      const responsible = params.get('responsible')
      if (responsible && !isNaN(+responsible)) filter.responsible = +responsible

      dispatch(getTasksListToCalendar(filter))
    },
    [localMode, location, sessionData, isLeader, dispatch],
  )

  const handleCancelModal = useCallback(() => {
    setIsOpenModal(false)
  }, [])

  const onPanelChange = useCallback(
    async (newValue: Dayjs, mode: 'year' | 'month') => {
      setValue(newValue)
      if (localMode !== mode && (mode === 'year' || mode === 'month')) {
        setLocalMode(mode)
      }

      const shouldUpdate =
        (mode === 'year' && newValue.format('YYYY') !== targetDate.current.format('YYYY')) ||
        (mode === 'month' && newValue.format('YYYY.MM') !== targetDate.current.format('YYYY.MM'))

      if (shouldUpdate) {
        targetDate.current = newValue

        if (!isLeader && !sessionData?.user?.id) {
          return
        }

        const params = new URLSearchParams(location.search)
        const filter: any = {
          dates: getFilterDateRange(newValue, mode),
        }

        const id = params.get('id')
        if (id) filter.id = id

        const stage = params.get('stage')
        if (stage) {
          filter.stage = stage
        } else {
          filter.stage = 'notDone'
        }

        const taskType = params.get('task_type')
        if (taskType) filter.task_type = taskType

        const responsible = params.get('responsible')
        if (responsible && !isNaN(+responsible)) filter.responsible = +responsible

        dispatch(getTasksListToCalendar(filter))
      }
    },
    [localMode, location, sessionData, isLeader, dispatch],
  )

  const handleModeChange = useCallback((newMode: 'year' | 'month' | 'week' | 'day') => {
    setLocalMode(newMode)
  }, [])

  const onClickDefaultCell = useCallback(
    (value: Dayjs) => {
      const detailData = getDetailTaskItemData(value, dataByDay)
      setCurrentDetailData(detailData)
      setIsOpenModal(true)
    },
    [dataByDay],
  )

  const onClickHoursCell = useCallback(
    (value: Dayjs, time) => {
      const detailData = getDetailTimeSlotData(value, dataByDay, time)
      if (detailData.length === 0) {
        return
      }
      setCurrentDetailData(detailData)
      setIsOpenModal(true)
    },
    [dataByDay],
  )

  const dateCellRender = useCallback(
    (value: Dayjs) => {
      if (isTimeTableViewMode) {
        const listData = getPreviewTaskItemDataByTime(value, dataByDay)
        return <HoursCellTemplate listData={listData} value={value} clickCell={onClickHoursCell} />
      }

      const listData = getPreviewTaskItemData(value, dataByDay, isMobile)
      if (!listData.length) {
        return
      }

      return (
        <DefaultCellTemplate
          listData={listData}
          value={value}
          clickCell={onClickDefaultCell}
          isMobile={isMobile}
        />
      )
    },
    [dataByDay, isLoading, isTimeTableViewMode, onClickHoursCell, onClickDefaultCell, isMobile],
  )

  const handleMonthSelect = useCallback(
    (selectedMonth: Dayjs) => {
      setValue(selectedMonth)
      setLocalMode('month')
      targetDate.current = selectedMonth

      if (!isLeader && !sessionData?.user?.id) {
        return
      }

      const params = new URLSearchParams(location.search)
      const filter: any = {
        dates: getFilterDateRange(selectedMonth, 'month'),
      }

      const id = params.get('id')
      if (id) filter.id = id

      const stage = params.get('stage')
      if (stage) {
        filter.stage = stage
      } else {
        filter.stage = 'notDone'
      }

      const taskType = params.get('task_type')
      if (taskType) filter.task_type = taskType

      const responsible = params.get('responsible')
      if (responsible && !isNaN(+responsible)) filter.responsible = +responsible

      dispatch(getTasksListToCalendar(filter))
    },
    [location, sessionData, isLeader, dispatch],
  )

  const monthCellRender = useCallback(
    (value: Dayjs) => {
      const monthStart = value.startOf('month').format('YYYY.MM.DD')
      const monthEnd = value.endOf('month').format('YYYY.MM.DD')

      let expiredCount = 0
      let newCount = 0

      Object.keys(dataByDay).forEach(dateKey => {
        const date = dayjs(dateKey, 'YYYY.MM.DD')
        if (
          date.isSameOrAfter(dayjs(monthStart, 'YYYY.MM.DD')) &&
          date.isSameOrBefore(dayjs(monthEnd, 'YYYY.MM.DD'))
        ) {
          const tasks = dataByDay[dateKey] || []
          expiredCount += tasks.filter(t => t.stage === 'expired').length
          newCount += tasks.filter(t => t.stage === 'new').length
        }
      })

      if (expiredCount === 0 && newCount === 0) {
        return null
      }

      return (
        <div className="year-month-cell" onClick={() => handleMonthSelect(value)}>
          <div className="year-month-cell-stats">
            {expiredCount > 0 && (
              <div className="year-month-cell-stat">
                <div className="year-month-cell-stat-badge expired" />
                <span>{isMobile ? expiredCount : `${expiredCount} просрочено`}</span>
              </div>
            )}
            {newCount > 0 && (
              <div className="year-month-cell-stat">
                <div className="year-month-cell-stat-badge new" />
                <span>{isMobile ? newCount : `${newCount} запланировано`}</span>
              </div>
            )}
          </div>
        </div>
      )
    },
    [dataByDay, handleMonthSelect, isMobile],
  )

  const headerRender = useCallback(
    ({ value: currentValue, type, onChange, onTypeChange }) => {
      const start = 0
      const end = 12
      const monthOptions: React.ReactElement[] = []

      const localeData = currentValue.localeData()
      const months: string[] = []
      for (let i = 0; i < 12; i++) {
        const monthDate = currentValue.clone().month(i)
        months.push(localeData.monthsShort(monthDate))
      }

      for (let i = start; i < end; i++) {
        monthOptions.push(
          <Select.Option key={i} value={i}>
            {months[i]}
          </Select.Option>,
        )
      }

      const year = currentValue.year()
      const month = currentValue.month()
      const navigationTitle = localMode === 'year' ? 'год' : 'месяц'
      const getShiftedValue = (amount: number) => {
        return localMode === 'year'
          ? currentValue.clone().add(amount, 'year')
          : currentValue.clone().add(amount, 'month')
      }
      const options: React.ReactElement[] = []
      for (let i = year - 10; i < year + 10; i += 1) {
        options.push(
          <Select.Option key={i} value={i}>
            {i}
          </Select.Option>,
        )
      }

      return (
        <div style={{ padding: 8, background: '#fff' }}>
          <Row gutter={8} align="middle" wrap={false}>
            <Col>
              <Button
                size="small"
                icon={<LeftOutlined />}
                aria-label={`Предыдущий ${navigationTitle}`}
                title={`Предыдущий ${navigationTitle}`}
                onClick={() => onChange(getShiftedValue(-1))}
              />
            </Col>
            <Col>
              <Select
                size="small"
                dropdownMatchSelectWidth={false}
                value={year}
                onChange={newYear => {
                  const now = currentValue.clone().year(newYear)
                  onChange(now)
                }}
              >
                {options}
              </Select>
            </Col>
            {localMode === 'month' && (
              <Col>
                <Select
                  size="small"
                  dropdownMatchSelectWidth={false}
                  value={month}
                  onChange={newMonth => {
                    const now = currentValue.clone().month(newMonth)
                    onChange(now)
                  }}
                >
                  {monthOptions}
                </Select>
              </Col>
            )}
            <Col>
              <Button
                size="small"
                icon={<RightOutlined />}
                aria-label={`Следующий ${navigationTitle}`}
                title={`Следующий ${navigationTitle}`}
                onClick={() => onChange(getShiftedValue(1))}
              />
            </Col>
          </Row>
        </div>
      )
    },
    [localMode],
  )

  return (
    <>
      {!isLoading && error && (
        <Alert type="error" message={'При выполнении запроса возникла ошибка' + error} />
      )}

      <ModeSelector
        value={localMode}
        onChange={handleModeChange}
        showTimetableToggle={localMode === 'month'}
        isTimeTableViewMode={isTimeTableViewMode}
        onTimetableToggle={() => setIsTimeTableViewMode(!isTimeTableViewMode)}
        hideToggleOnMobile={true}
      />

      {localMode === 'day' ? (
        <DayView
          value={value}
          dataByDay={dataByDay}
          isTimeTableViewMode={isTimeTableViewMode}
          onValueChange={handleValueChange}
        />
      ) : localMode === 'week' ? (
        <WeekView
          value={value}
          dataByDay={dataByDay}
          isTimeTableViewMode={isTimeTableViewMode}
          onValueChange={handleValueChange}
        />
      ) : (
        <CalendarContainer>
          <Calendar
            value={value}
            mode={localMode}
            dateCellRender={localMode === 'month' ? dateCellRender : undefined}
            monthCellRender={localMode === 'year' ? monthCellRender : undefined}
            headerRender={headerRender}
            onPanelChange={onPanelChange}
            onSelect={onSelect}
          />
        </CalendarContainer>
      )}

      {(localMode === 'month' || localMode === 'year') && (
        <TaskDetailModal
          onCancel={handleCancelModal}
          isOpen={isOpenModal}
          data={currentDetailData}
        />
      )}
    </>
  )
}
